import { notifications } from '@mantine/notifications';
import type { SelectedTestContent } from '~/server/logic/prisma/test-content-select';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import type { api } from '~/trpc-extra/api';

export type TestContentMutationContext = {
  prevData: SelectedTestContent | null;
};

export const cancelTestContentQueries = async ({
  trpcUtils,
  testContentId,
  parentContext,
}: {
  trpcUtils: ReturnType<typeof api.useUtils>;
  testContentId: string;
  // parentContext: see comment below
  parentContext?: OperationParentContext;
}) => {
  await Promise.allSettled([
    trpcUtils.testContent.get.cancel({ id: testContentId }),
    trpcUtils.testContent.getCode.cancel({ testContentId }),
    parentContext?.componentId && trpcUtils.component.get.cancel({ id: parentContext.componentId }),
    parentContext?.conditional &&
      trpcUtils.operation.getFullConditionalBlock.cancel({
        conditionalBlockId: parentContext.conditional.conditionalBlockId,
      }),
  ]);
};

export const handleMutationSuccess = async ({
  trpcUtils,
  testContentId,
  message,
  parentContext,
}: {
  trpcUtils: ReturnType<typeof api.useUtils>;
  testContentId: string;
  message?: string;
  // invalidate parent (component or conditional block) after updating a child operation, because we have a separate
  // fetch for their inner operations only once they're expanded. So invalidating the testContent wouldn't update the inner operation changes
  parentContext?: OperationParentContext;
}) => {
  await Promise.allSettled([
    trpcUtils.testContent.get.invalidate({ id: testContentId }),
    trpcUtils.testContent.getCode.invalidate({ testContentId }),
    parentContext?.componentId && trpcUtils.component.get.invalidate({ id: parentContext.componentId }),
    parentContext?.conditional &&
      trpcUtils.operation.getFullConditionalBlock.invalidate({
        conditionalBlockId: parentContext.conditional.conditionalBlockId,
      }),
  ]);
  if (message) {
    notifications.show({ message, color: 'teal' });
  }
};

export const handleMutationError = async ({
  trpcUtils,
  testContentId,
  message,
  context,
  parentContext,
}: {
  trpcUtils: ReturnType<typeof api.useUtils>;
  testContentId: string;
  message: string;
  context: { prevData: SelectedTestContent | null } | undefined;
  // parentContext: see comment above
  parentContext?: OperationParentContext;
}) => {
  if (context?.prevData) {
    trpcUtils.testContent.get.setData({ id: testContentId }, context.prevData);
  }
  notifications.show({ message, color: 'red' });
  await Promise.allSettled([
    trpcUtils.testContent.get.invalidate({ id: testContentId }),
    trpcUtils.testContent.getCode.invalidate({ testContentId }),
    parentContext?.componentId && trpcUtils.component.get.invalidate({ id: parentContext.componentId }),
    parentContext?.conditional &&
      trpcUtils.operation.getFullConditionalBlock.invalidate({
        conditionalBlockId: parentContext.conditional.conditionalBlockId,
      }),
  ]);
};
